import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

/**
 * Daily Metrics Line Chart
 * Shows trend of help button clicks, turn-taking score, and response time over the last N days
 */
export const DailyMetricsChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="empty-chart">
        <p>No daily metrics available yet. Start a conversation to see progress!</p>
      </div>
    );
  }

  // Format data for chart (reverse to show oldest to newest)
  const chartData = [...data].reverse().map(metric => ({
    date: new Date(metric.session_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    sessions: metric.session_count || 0,
    helpClicks: metric.help_button_clicks,
    engagement: metric.engagement_score,
    responseTime: metric.avg_response_time_seconds,
    messages: metric.total_messages
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        <Tooltip />
        <Legend />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="engagement"
          stroke="#667eea"
          strokeWidth={2}
          name="Engagement Score"
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="sessions"
          stroke="#2ecc71"
          strokeWidth={2}
          name="Sessions per Day"
        />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="helpClicks"
          stroke="#f39c12"
          strokeWidth={2}
          name="Help Button Clicks"
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="responseTime"
          stroke="#3498db"
          strokeWidth={2}
          name="Avg Response Time (sec)"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

/**
 * Weekly Metrics Bar Chart
 * Shows aggregated weekly metrics with week-over-week improvement
 */
export const WeeklyMetricsChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="empty-chart">
        <p>No weekly metrics available yet. Keep chatting to build trends!</p>
      </div>
    );
  }

  // Format data for chart (reverse to show oldest to newest)
  const chartData = [...data].reverse().map(metric => ({
    week: new Date(metric.week_start_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    sessions: metric.total_sessions,
    helpClicks: metric.total_help_clicks,
    engagement: metric.avg_engagement_score,
    messages: metric.total_messages,
    improvement: metric.improvement_percentage
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="week" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey="sessions" fill="#667eea" name="Total Sessions" />
        <Bar dataKey="helpClicks" fill="#f39c12" name="Help Button Clicks" />
        <Bar dataKey="engagement" fill="#3498db" name="Avg Engagement Score" />
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Metrics Summary Card
 * Shows key metric stats in a card format
 */
export const MetricsSummaryCard = ({ title, value, unit, trend, icon }) => {
  return (
    <div className="metrics-summary-card">
      <div className="card-header">
        <span className="card-icon">{icon}</span>
        <h4>{title}</h4>
      </div>
      <div className="card-value">
        <span className="value">{value}</span>
        {unit && <span className="unit">{unit}</span>}
      </div>
      {trend !== null && trend !== undefined && (
        <div className={`card-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
          <span>{trend >= 0 ? '↑' : '↓'}</span>
          <span>{Math.abs(trend)}%</span>
          <span className="trend-label">vs last week</span>
        </div>
      )}
    </div>
  );
};

/**
 * Message Distribution Pie Chart
 * Shows distribution of child messages vs AI messages
 */
export const MessageDistributionChart = ({ childMessages, aiMessages }) => {
  if (!childMessages && !aiMessages) {
    return (
      <div className="empty-chart">
        <p>No message data available yet.</p>
      </div>
    );
  }

  const total = childMessages + aiMessages;
  const childPercentage = ((childMessages / total) * 100).toFixed(1);
  const aiPercentage = ((aiMessages / total) * 100).toFixed(1);

  return (
    <div className="message-distribution">
      <div className="distribution-bar">
        <div
          className="child-portion"
          style={{ width: `${childPercentage}%` }}
        >
          <span>{childMessages} child</span>
        </div>
        <div
          className="ai-portion"
          style={{ width: `${aiPercentage}%` }}
        >
          <span>{aiMessages} AI</span>
        </div>
      </div>
      <div className="distribution-labels">
        <div className="label">
          <span className="color-indicator child"></span>
          <span>Child Messages: {childPercentage}%</span>
        </div>
        <div className="label">
          <span className="color-indicator ai"></span>
          <span>AI Messages: {aiPercentage}%</span>
        </div>
      </div>
    </div>
  );
};
